import { useState, type ReactNode } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { Icon, type IconName } from '@/components/ui/Icon';
import { toast } from '@/stores/toasts';
import { notifyKeys } from './api';
import { enablePush } from './push';
import { promptInstall, useNudges } from './nudges';

/** A small, dismissible card above the bottom nav — never a modal. */
function NudgeCard({ icon, title, body, children, testId }: { icon: IconName; title: string; body: string; children: ReactNode; testId: string }) {
  const reduced = useReducedMotion();
  return (
    <motion.aside
      initial={reduced ? { opacity: 0 } : { opacity: 0, y: 12, filter: 'blur(4px)' }}
      animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
      exit={reduced ? { opacity: 0, transition: { duration: 0.15 } } : { opacity: 0, y: -12, filter: 'blur(4px)', transition: { duration: 0.15, ease: 'easeOut' } }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      role="dialog"
      aria-label={title}
      className="fixed inset-x-3 bottom-[calc(env(safe-area-inset-bottom)+84px)] z-[60] mx-auto max-w-md rounded-card bg-surface p-5 shadow-lift sm:bottom-6 sm:left-auto sm:right-6 sm:mx-0 sm:w-[380px]"
      data-testid={testId}
    >
      <div className="flex items-start gap-3">
        <Icon name={icon} size={20} strokeWidth={1.75} className="mt-0.5 shrink-0" />
        <div className="min-w-0">
          <p className="text-[17px] font-semibold leading-snug">{title}</p>
          <p className="mt-1 text-[15px] text-muted">{body}</p>
        </div>
      </div>
      <div className="mt-4 flex justify-end gap-2">{children}</div>
    </motion.aside>
  );
}

/**
 * The soft ask before the browser's permission prompt (SPEC_V9 §D3): shown
 * after a date is scheduled or after the first date — never on first load.
 * The real prompt only appears when the person taps "Turn on".
 */
export function PushSoftAsk({ vapidKey, otherName }: { vapidKey: string; otherName?: string | null }) {
  const answer = useNudges((s) => s.answerPush);
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const on = async () => {
    if (busy) return;
    setBusy(true);
    const r = await enablePush(vapidKey);
    setBusy(false);
    answer('done');
    if (r.ok) {
      qc.setQueryData(notifyKeys.notifications, r.view);
      toast.success('Notifications are on', 'We’ll nudge this device before your dates.');
    } else if (r.reason === 'denied') {
      toast.info('Notifications are blocked', 'You can allow them for Popin in your browser’s site settings.');
    }
  };
  return (
    <NudgeCard
      icon="clock"
      title="Want a nudge before your date?"
      body={`We’ll ping this device for reminders${otherName ? ` and if ${otherName} is running late` : ''}. Nothing else, ever.`}
      testId="push-soft-ask"
    >
      <Button variant="ghost" accent="paper" size="sm" disabled={busy} onClick={() => answer('later')} data-testid="push-soft-ask-later">
        Not now
      </Button>
      <Button size="sm" icon="check" onClick={() => on()} loading={busy} data-testid="push-soft-ask-on">
        Turn on
      </Button>
    </NudgeCard>
  );
}

/** "Add Popin to your home screen", after the first date (beforeinstallprompt). */
export function InstallPrompt() {
  const answer = useNudges((s) => s.answerInstall);
  return (
    <NudgeCard icon="download" title="Add Popin to your home screen" body="Your next date is one tap away. It opens like an app, no store needed." testId="install-prompt">
      <Button variant="ghost" accent="paper" size="sm" onClick={() => answer('later')} data-testid="install-later">
        Not now
      </Button>
      <Button
        size="sm"
        icon="download"
        onClick={async () => {
          const ok = await promptInstall();
          answer(ok ? 'done' : 'later');
        }}
        data-testid="install-add"
      >
        Add to home screen
      </Button>
    </NudgeCard>
  );
}
